import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  alpha,
  useTheme,
  AppBar,
  Toolbar,
  Icon,
  Grid,
  IconButton,
  Button,
  Stack,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  ListItemText,
} from "@mui/material";
import { SessionCheck } from "../../components/SessionCheck/SessionCheck";
import { useProducts } from "../../hooks/useProducts";
import { useCart } from "../../hooks/useCart";
import { PaymentMethodSelector } from "../../components/PaymentMethodSelector/PaymentMethodSelector";
import { CashPaymentDialog } from "../../components/PaymentDialogs/CashPaymentDialog";
import { CardPaymentDialog } from "../../components/PaymentDialogs/CardPaymentDialog";
import { QRPaymentDialog } from "../../components/PaymentDialogs/QRPaymentDialog";
import { BillSummaryDialog } from "../../components/PaymentDialogs/BillSummaryDialog";
import { usePOSService } from "../../hooks/usePOSService";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { OrderUpdatePaymentData, posService } from "../../../../services/pos.service";
import { CartItem, CURRENCY, DiscountThreshold, ModifierGroup, OrderDiscount, paymentLinkRequest, Product } from "../../types/pos.types";
import { OrderModeSelector } from "../../components/OrderModeSelector/OrderModeSelector";
import { DeliveryForm, DeliveryInfo } from "../../components/DeliveryForm/DeliveryForm";
import { Table, TableSelector } from "../../components/TableSelector/TableSelector";
import { useDebounce } from "../../../../hooks/useDebounce";
import { ItemDetailsModal } from "../../components/ItemDetailsModal/ItemDetailsModal";
import { useAuth } from "../../../auth/hooks/useAuth";
import { SupervisorPinModal } from "../../components/SuperuserPinModal/SuperuserPinModal";
import { authService } from "../../../../services/auth.service";
import { ConfirmActionDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";
import { useUserRole } from "../../hooks/useUserRole";
import { publicService } from "../../../../services/public.service";
import { hasPermission } from "../../../auth/utils/permission.utils";

interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  itemCount: number;
}

export interface MenuItem {
  id: string;
  name: string;
  price: number;
  category: string;
  description?: string;
  quantity?: number;
  modifiers?: ModifierGroup[];
}

export const MenuPage: React.FC = () => {
  const userRole = useUserRole();
  const theme = useTheme();
  const { products, loading } = useProducts();
  const { user } = useAuth();

  const {
    items,
    addItem,
    removeItem,
    updateQuantity,
    updatePrice,
    updateModifiers,
    handleEditCartItem,
    handleDeleteCartItem,
    total,
    subtotal,
    tax,
    clearCart,
    appliedDiscount,
    setAppliedDiscount
  } = useCart();
  const { posService: service } = usePOSService();
  const navigate = useNavigate();

  //on first load, remove the stored draft id and ensure the user is not creating new draft order from the same page
  useEffect(() => {
    const draftId = localStorage.getItem("currentDraftId");

    if (draftId) {
      console.warn("Existing draft ID found on load:", draftId);

      localStorage.removeItem("currentDraftId");
    }

    return () => {
      localStorage.removeItem("currentDraftId");
    };
  }, []);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    null
  );
  const [selectedPayment, setSelectedPayment] = useState<string>("cash");
  const [cashPaymentOpen, setCashPaymentOpen] = useState(false);
  const [cardPaymentOpen, setCardPaymentOpen] = useState(false);
  const [billSummaryOpen, setBillSummaryOpen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<{
    amountPaid?: number;
    change?: number;
  }>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderResponse, setOrderResponse] = useState<any>(null);
  const [qrPaymentOpen, setQrPaymentOpen] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [trackId, setTrackId] = useState<string | undefined>(undefined);

  //some states to help checking payment status
  const [isPolling, setIsPolling] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [pollingError, setPollingError] = useState<string | null>(null);

  const [qrPaymentType, setQrPaymentType] = useState<string>("");
  const qrPaymentTypeRef = useRef<string>("");
  const [openQrOptionModal, setOpenQrOptionModal] = useState(false);

  useEffect(() => {
    qrPaymentTypeRef.current = qrPaymentType;
  }, [qrPaymentType]);

  const [resolveQrPromise, setResolveQrPromise] = useState<((value: "mastercard" | "benefitpay") => void) | null>(null);
  const handleSelectPayment = (method: string) => {
    if (method === "qr") {
      setOpenQrOptionModal(true);
    } else {
      setSelectedPayment(method); // Normal payment methods
    }
  };
  const promptForQrPaymentType = (): Promise<"mastercard" | "benefitpay"> => {
    return new Promise((resolve) => {
      setQrPaymentType('');
      setResolveQrPromise(() => resolve);
      setOpenQrOptionModal(true);
    });
  };

  //before order, select order mode states 
  const [orderType, setOrderType] = useState<string | null>(null);
  //if order type is changes and confirmed, reset it all
  const resetOrderState = () => {
    clearCart();
    setSelectedPayment("cash");
    setDeliveryInfo(null);
    setSelectedTable(null); // only needed if you implement Task 2 later
    localStorage.removeItem("currentDraftId"); // Clear draft ID
  };
  //mode switching confirmaiton 
  const [showConfirm, setShowConfirm] = useState(false);

  const [showConfirmDialog, setShowConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmText?: string;
    cancelText?: string;
    icon?: string;
    color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
    onConfirm: () => void;
  } | null>(null);

  //delivery option state
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo | null>(null);

  //state for table selecting
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);

  // For table confirmation dialog
  const [showTableConfirm, setShowTableConfirm] = useState(false);
  const [newSelectedTable, setNewSelectedTable] = useState<Table | null>(null);
  const [showTableModal, setShowTableModal] = useState(false);

  //search Query
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [isFiltering, setIsFiltering] = useState<boolean>(false);

  //modal + modifiers
  const [selectedItem, setSelectedItem] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState<number>(1);

  const location = useLocation();
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const [discountThresholds, setDiscountThresholds] = useState<DiscountThreshold | null>(null);
  const [isDiscountThresholdLoading, setIsDiscountThresholdLoading] = useState(true);

  const [showPINModal, setShowPINModal] = useState(false);
  const [reasonForDiscount, setReasonForDiscount] = useState("");
  const [appliedDiscountValue, setAppliedDiscountValue] = useState<number>(0);
  const [appliedDiscountType, setAppliedDiscountType] = useState<"PERCENTAGE" | "FIXED" | null>(null);

  const isWaiter = userRole === "waiter";
  const isCashier = userRole === "cashier";
  const isSupervisor = userRole === "supervisor";

  const search = new URLSearchParams(location.search);
  const tableIdFromUrl = search.get("tableId");
  const [isPaymentInProgress, setIsPaymentInProgress] = useState(false);

  useEffect(() => {
    const paymentOrderId = localStorage.getItem("paymentInProcess");
    if (paymentOrderId) {
      setIsPaymentInProgress(true);
    }
  }, []);

  useEffect(() => {
    const loadTableFromUrl = async () => {
      if (!tableIdFromUrl) return;

      try {
        const response = await posService.getTableById(tableIdFromUrl);
        if (response.success && response.data) {
          setSelectedTable(response.data);
          setOrderType("dine_in"); // 👈 Force dine_in mode
        }
      } catch (err) {
        console.warn("Failed to load table by ID from URL:", err);
      }
    };

    loadTableFromUrl();
  }, [tableIdFromUrl]);


  const showConfirmation = ({
    title,
    description,
    confirmText = "Continue",
    cancelText = "Cancel",
    icon = "warning",
    color = "error",
    onConfirm,
  }: Omit<React.ComponentProps<typeof ConfirmActionDialog>, "open" | "onClose"> & {
    onConfirm: () => void;
  }) => {
    setShowConfirmDialog({
      open: true,
      title,
      description,
      confirmText,
      cancelText,
      icon,
      color,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    if (showConfirmDialog) {
      setShowConfirmDialog(null);
    }
  };

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const orderId = search.get("orderId");

    if (!orderId) return;

    const loadDraftOrder = async () => {
      setIsProcessing(true);


      try {
        const response = await service.getOrder(orderId);

        if (response) {
          const order = response.data.data;

          // Clear current cart
          clearCart();

          // Set order type
          if (order.orderType) {
            setOrderType(order.orderType);
          }

          // Restore delivery info
          if (order.delivery_info && Object.keys(order.delivery_info).length > 0) {
            setDeliveryInfo(order.delivery_info);
          }

          // Restore selected table
          if (order.table) {
            setSelectedTable(order.table);
          }

          // Restore each item
          order.items.forEach((itemFromDraft: any) => {
            const productId = itemFromDraft.item_id;
            const uniqueId = `${productId}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
            console.log("Item from draft:", itemFromDraft);

            // Get real product from catalog
            console.log('before', products);
            const productInCatalog = products.find(p => p._id === productId);
            console.log(products);

            if (!productInCatalog) {
              console.warn("Product not found:", productId);
              return;
            }
            const modifiersMap = itemFromDraft.modifiers;
            console.log('this is the map', modifiersMap);

            // Add to cart
            addItem(
              {
                ...productInCatalog,
                price: itemFromDraft.price,
                basePrice: itemFromDraft.price,
              },

              modifiersMap

            );
          });
        }


      } catch (err) {
        console.error("Error loading order:", err);
        toast.error("Failed to load order");
      } finally {
        setIsProcessing(false);


      }
    };

    loadDraftOrder();
  }, [location.search, products]);

  const orderId = orderResponse?.data?.order?.order_id || localStorage.getItem("paymentInProcess");

  useEffect(() => {
    const loadDiscountThresholds = async () => {
      try {
        const user = await authService.getCurrentUser();
        const store_id = user?.business?._id || user?.store_id;

        if (!store_id) throw new Error("Store ID not found");

        const thresholds = await posService.getDiscountThresholds({ store_id });
        setDiscountThresholds(thresholds);
      } catch (err: any) {
        console.error("Failed to load discount thresholds:", err.message);
      } finally {
        setIsDiscountThresholdLoading(false);
      }
    };

    loadDiscountThresholds();
  }, []);

  //no webhook? no problem, we're going to check the payment every 5 seconds until the use either pays or fails to pay a simple transaction
  const validatePayment = async (track_id: string): Promise<boolean | undefined> => {
    try {
      const response = await service.validatePayment({ track_id });
      return response.success && response.data?.success;
    } catch (err) {
      console.error("Validation failed:", err);
      return false;
    }
  };

  //this is where we validate the payment every 5 seconds
  // Poll for QR payment status
  useEffect(() => {
    if (!qrPaymentOpen || !trackId) {
      console.log("Polling stopped: missing qrPaymentOpen or trackId");
      return;
    }

    let intervalId: NodeJS.Timeout;

    const pollPaymentStatus = async () => {
      console.log("Polling payment status for trackId:", trackId);
      try {
        const isPaid = await validatePayment(trackId);
        console.log("Poll result - isPaid:", isPaid);

        if (isPaid) {
          clearInterval(intervalId);
          setPaymentConfirmed(true);
          setIsPolling(false);

          if (orderResponse?.data?.id) {
            const orderId = orderResponse.data.order.order_id;
            const updatePaymentData = {
              order_id: orderId,
              payment: {
                method: qrPaymentTypeRef.current === "mastercard" ? "CARD" : "MOBILE",
                status: "paid",
                transaction_id: trackId,
              },
              action_description: "Payment completed via QR code",
            };

            const updatePaymentResponse = await service.updateOrderPayment(updatePaymentData);
            if (!updatePaymentResponse.success) {
              throw new Error("Failed to update payment status");
            }

            // ✅ STEP 3: Update order status to 'paid'
            const updateOrderStatusResponse = await posService.updateOrderStatus({
              order_id: orderId,
              status: "paid"
            });

            setBillSummaryOpen(true);
            return
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
        setPollingError("Failed to check payment status");
        clearInterval(intervalId);
        setIsPolling(false);
        return;
      }
    };

    setIsPolling(true);
    pollPaymentStatus(); // Initial fetch
    intervalId = setInterval(pollPaymentStatus, 5000);

    return () => {
      console.log("Cleaning up polling...");
      sessionStorage.removeItem("CurrentTrackID");
      clearInterval(intervalId);
    };
  }, [qrPaymentOpen, trackId]);

  const applyDiscount = (
    type: "PERCENTAGE" | "FIXED",
    value: number,
    reason?: string
  ) => {
    if (!discountThresholds) {
      toast.warn("Discount thresholds not loaded");
      return;
    }

    if (value <= 0) {
      toast.warn("Discount must be greater than zero");
      return;
    }

    let overThreshold = false;

    if (type === "PERCENTAGE" && value > discountThresholds.cashierMaxPercentageDiscount) {
      overThreshold = true;
    } else if (type === "FIXED" && value > discountThresholds.cashierMaxFixedDiscount) {
      overThreshold = true;
    }

    if (overThreshold) {
      // 🔐 Show supervisor PIN modal
      setAppliedDiscount({
        type,
        value,
        reason: reason || "",
      });
      setShowPINModal(true);
      return;
    }

    // ✅ Apply directly
    setAppliedDiscount({
      type,
      value,
      reason: reason || "No reason provided",
    });

    // 📝 Log to audit trail
    const finalAmount = subtotal + tax - (type === "PERCENTAGE"
      ? ((subtotal + tax) * value) / 100
      : value);

    toast.success("Discount applied!");
  };

  const handleSendToKitchen = async () => {
    if (items.length === 0) {
      toast.warn("Cart is empty.");
      return;
    }

    setIsProcessing(true);


    try {
      const orderData = {
        table: selectedTable || { id: "", name: "N/A", status: "free" },
        orderType,
        items: items.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price,
          modifiers: item.modifiers || {},
        })),
        payment: {
          amount: total,
        },
      };

              const response = await posService.sendOrderToKitchen(orderData);

      if (response.success && selectedTable) {
        const result = await posService.changeTableStatus(
          selectedTable.table_id,
          "occupied",
          {
            userId: user?.userId,
            role: userRole
          },
          orderId
        );
      }

      if (isWaiter && response.success) {
        navigate("/pos/waiterDashboard");
      }
      else if (response.success) {
        toast.success("Order sent to kitchen!");
        clearCart();
        navigate("/pos/orders");
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsProcessing(false);


    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    toast.info("Discount removed");
  };

  const handleSaveDraft = async () => {
    if (!orderType) {
      toast.error("Please select an order type first");
      return;
    }

    const existingDraftId = localStorage.getItem("currentDraftId");

    // If there's an existing draft and we're not overwriting
    if (existingDraftId) {
      showConfirmation({
        title: "Existing Draft Found",
        description: "There is already a draft in progress. Continuing will overwrite it.",
        confirmText: "Overwrite Draft",
        cancelText: "Cancel",
        icon: "edit",
        color: "warning",
        onConfirm: async () => {
          try {
            const draftData = {
              orderType,
              delivery_info: deliveryInfo,
              table: selectedTable,
              items: items.map((item) => ({
                product_id: item.product_id,
                quantity: item.quantity,
                price: item.price,
                modifiers: item.modifiers,
              })),
              payment: {
                method: "NONE", // Required by schema
                amount: total,      // Required by schema
                status: "pending_payment",
                transaction_id: "",
              },
              subtotal,
              tax,
              total,
              created_at: Date.now(),
            };

            const draftId = generateTempOrderId(); // Or use existing ID if needed

            const response = await service.saveDraftOrder({
              ...draftData,
              order_id: draftId,
              status: "Draft",
            });

            if (!response.success) throw new Error("Failed to save draft");

            localStorage.setItem("currentDraftId", draftId);

            toast.success("Draft updated");
          } catch (err) {
            console.error("Error saving draft:", err);
            toast.error("Failed to save draft");
          }
        },
      });
      return;
    }

    // No existing draft — proceed normally
    const draftId = generateTempOrderId();
    const draftData = {
      orderType,
      delivery_info: deliveryInfo,
      table: selectedTable,
      items: items.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        price: item.price,
        modifiers: item.modifiers,
      })),
      payment: {
        method: "NONE", // Required by schema
        amount: total,      // Required by schema
        status: "pending_payment",
        transaction_id: null,
      },
    };

    try {
      setIsProcessing(true);


      const response = await service.saveDraftOrder({
        ...draftData,
        status: "Draft",
      });

      if (!response.success) throw new Error("Failed to save draft");

      localStorage.setItem("currentDraftId", draftId);

      toast.success("New draft saved");
      navigate("/pos/orders");
    } catch (err) {
      console.error("Error saving draft:", err);
      toast.error("Failed to save draft");
    } finally {
      setIsProcessing(false);


    }
  };

  // Helper to generate unique ID
  const generateTempOrderId = () => {
    return `draft-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
  };

  const getOrderTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      dine_in: "Dine In",
      takeaway: "Takeaway",
      delivery: "Delivery"
    };
    return labels[type] || "Unknown Mode";
  };


  // Helper functions for category icons and colors
  const getCategoryIcon = (category: string): string => {
    const icons: Record<string, string> = {
      Breakfast: "free_breakfast",
      Soups: "soup_kitchen",
      Pasta: "restaurant",
      Sushi: "set_meal",
      "Main Course": "dinner_dining",
      dessert: "cake",
      Drinks: "local_bar",
      Alcohol: "wine_bar",
      Pizza: "local_pizza",
      Burgers: "lunch_dining",
      Sandwiches: "fastfood",
      Salads: "emoji_nature",
      Seafood: "set_meal",
      Appetizers: "restaurant_menu",
      BBQ: "outdoor_grill",
      Vegan: "spa",
      Vegetarian: "eco",
      Asian: "ramen_dining",
      Indian: "restaurant",
      Mexican: "local_dining",
      Italian: "local_pizza",
      French: "bakery_dining",
      Mediterranean: "emoji_food_beverage",
      Snacks: "fastfood",
      IceCream: "icecream",
      Coffee: "coffee",
      Tea: "emoji_food_beverage",
      Juices: "local_cafe",
      Cocktails: "liquor",
      Beers: "sports_bar",
      Wines: "nightlife",
      HotDrinks: "local_cafe",
      ColdDrinks: "local_bar",
      Smoothies: "blender",
      Grilled: "outdoor_grill",
      Rice: "ramen_dining",
      Noodles: "lunch_dining",
      Steaks: "dinner_dining",
      FingerFood: "tap_and_play",
    };

    return icons[category] || "restaurant_menu";
  };

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      Breakfast: "#90caf9", // Light blue
      Soups: "#f48fb1", // Pink
      Pasta: "#81d4fa", // Light blue
      Sushi: "#b39ddb", // Purple
      "Main course": "#ffab91", // Light pink
      dessert: "#ce93d8", // Light purple
      Drinks: "#f48fb1", // Pink
      Alcohol: "#80cbc4", // Teal
    };
    return colors[category] || "#757575";
  };

  // Group products by category and add quantity
  const categories: Category[] = React.useMemo(() => {
    const categoryMap = products.reduce((acc, product) => {
      const cat = acc.find((c) => c.id === product.category);
      if (!cat) {
        acc.push({
          id: product.category,
          name: product.category,
          icon: getCategoryIcon(product.category),
          color: getCategoryColor(product.category),
          itemCount: 1,
        });
      } else {
        cat.itemCount++;
      }
      return acc;
    }, [] as Category[]);
    return categoryMap;
  }, [products]);

  // Filter products by selected category and add quantities
  const menuItems = React.useMemo(() => {
    const productsWithQuantity = products.map((product) => ({
      ...product,
      quantity:
        items.find((item) => item.product_id === product._id)?.quantity || 0,
    }));
    if (!selectedCategory) return productsWithQuantity;
    return productsWithQuantity.filter(
      (product) => product.category === selectedCategory
    );
  }, [products, selectedCategory, items]);

  // Default case: show either filtered or full list
  const displayItems = useMemo(() => {
    // Start with all items in the selected category
    let result = [...menuItems];

    // Apply search filter if there's a query
    const query = debouncedSearch.toLowerCase();
    if (query) {
      result = result.filter((item) =>
        item.name.toLowerCase().includes(query)
      );
    }

    return result;
  }, [menuItems, debouncedSearch]);


  const handleQuantityChange = (productId: string, newQuantity: number) => {
    const product = products.find((p) => p._id === productId);
    if (!product) return;

    if (newQuantity === 0) {
      removeItem(productId);
    } else if (items.find((item) => item.product_id === productId)) {
      updateQuantity(productId, newQuantity);
    } else {
      addItem(product);
      updateQuantity(productId, newQuantity);
    }
  };

  const handleQrPayment = async (success: boolean) => {
    setIsProcessing(true);
    const paymentInProcessOrderId = localStorage.getItem("paymentInProcess");
    try {
      if (!success || !trackId) {
        throw new Error("QR payment failed or missing transaction ID");
      }

      const orderId = paymentInProcessOrderId;

      if (!orderId) {
        throw new Error("No order ID found to update");
      }

      // STEP 1: Determine payment method
      let paymentMethod: "CASH" | "CARD" | "MOBILE";
      switch (qrPaymentTypeRef.current.toLowerCase()) {
        case "mastercard":
          paymentMethod = "CARD";
          break;
        case "benefitpay":
          paymentMethod = "MOBILE";
          break;
        default:
          paymentMethod = "MOBILE";
      }

      // STEP 2: Update payment status
      const updatePaymentData: OrderUpdatePaymentData = {
        order_id: orderId,
        payment: {
          method: paymentMethod,
          status: "paid",
          transaction_id: trackId,
        },
        action_description: "Payment completed via QR code",
      };

      const updatePaymentResponse = await service.updateOrderPayment(updatePaymentData);
      if (!updatePaymentResponse.success) {
        throw new Error("Failed to update payment status");
      }

      // ✅ STEP 3: Update order status to 'paid'
      const updateOrderStatusResponse = await posService.updateOrderStatus({
        order_id: orderId,
        status: "paid"
      },);

      if (!updateOrderStatusResponse.success) {
        throw new Error("Failed to update order status");
      }

      // STEP 4: Fetch updated order
      const updatedOrderResponse = await service.getOrder(orderId);
      if (!updatedOrderResponse.success) {
        throw new Error("Failed to fetch updated order");
      }

      setOrderResponse(updatedOrderResponse); // Refresh UI state
      setBillSummaryOpen(true);
    } catch (err: any) {
      const errorMessage = err.message || "An error occurred while updating payment";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
      setQrPaymentOpen(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!isCashier && !isSupervisor) {
      Snackbar({ open: true, message: "Only cashiers or supervisors can place orders" });
      return;
    }

    if (selectedPayment === "cash") {
      setCashPaymentOpen(true);
      return;
    }
    if (selectedPayment === "mastercard" || selectedPayment === "benefit") {
      setCardPaymentOpen(true);
      return;
    }
    if (selectedPayment === "qr") {
      const chosenType = await promptForQrPaymentType();
      setQrPaymentType(chosenType); // Now selectedPayment is either mastercard or benefitpay
    }


    setIsProcessing(true);
    setError(null);

    try {
      const paymentType = qrPaymentTypeRef.current;
      let paymentMethod: "CASH" | "CARD" | "MOBILE";
      switch (paymentType.toLowerCase()) {
        case "mastercard":
          paymentMethod = "CARD";
          break;
        case "benefitpay":
          paymentMethod = "MOBILE";
          break;
        default:
          paymentMethod = paymentType.toUpperCase() as "CASH" | "CARD" | "MOBILE";
      }

      const paymentData: paymentLinkRequest = {
        amount: total,
        payment_provider: paymentType === "benefitpay" ? "benefit" : paymentType,
      };

      const response = await service.requestPaymentLink(paymentData);
      if (response.data.payment_url)
        setPaymentUrl(response.data.payment_url);
      setTrackId(response.data.track_id);

      const { payment_url, track_id } = response.data || {};


      if (!response.success || !payment_url || !track_id) {
        throw new Error("Failed to generate payment link");
      }


      // Create the order
      if (!orderId) {
        // If no order exists, create new one
        setIsProcessing(true);
        try {
          const newOrder = await createOrder({
            method: "CASH",
            amount: total,
            reference: ''
          });
          if (!newOrder?.data?.order?.order_id) throw new Error("Failed to create order");
          localStorage.setItem("paymentInProcess", newOrder.data.order.order_id);
        } catch (err: any) {
          toast.error(err.message || "Error creating order");
          return;
        } finally {
          setIsProcessing(false);
        }
      } else {
        // Use existing order ID
        localStorage.setItem("paymentInProcess", orderId);
      }

      if (orderType === "dine_in" && selectedTable) {
        try {
          const result = await service.changeTableStatus(
            selectedTable.table_id,
            "occupied",
            {
              userId: user?.userId,
              role: userRole
            },
            orderId
          );

          if (!result.success) {
            toast.warn("Table status could not be updated");
          }
        } catch (err: any) {
          console.error("Error updating table status:", err.message);
        }
      }

      setQrPaymentOpen(true);

    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Something went wrong";

      setError(errorMessage);
      toast.error(errorMessage);
      sessionStorage.removeItem("CurrentTrackID");
      localStorage.removeItem("currentTrackId");

    } finally {
      setIsProcessing(false);


    }
  };

  const createOrder = async (
    paymentDetails: {
      method: "CASH" | "CARD" | "MOBILE";
      amount: number;
      reference?: string;
    },
    isDraft: boolean = false,
    skipPaymentLink: boolean = false
  ) => {
    setIsProcessing(true);


    setError(null);
    try {
      const orderData = {
        orderType: orderType,
        delivery_info: deliveryInfo,
        items: items.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price,
          modifiers: item.modifiers
        })),
        payment: isDraft
          ? { method: paymentDetails.method, amount: 0, status: "Draft", transaction_id: "" }
          : {
            method: paymentDetails.method,
            amount: total,
            status: "pending_payment",
            transaction_id: paymentDetails.reference || "",
          },
        table: selectedTable,
        status: isDraft ? "Draft" : "pending_payment",
        ...(appliedDiscount && {
          discount: {
            type: appliedDiscount.type,
            value: appliedDiscount.value,
            reason: appliedDiscount.reason || "No reason provided",
          },
        }),
      };


      const response = await service.createOrder(orderData);

      if (response) {
        setOrderResponse(response);
        // Only generate payment link if not skipped
        if (!skipPaymentLink && !isDraft && !(paymentDetails.method === "CARD" || paymentDetails.method === "MOBILE" || paymentDetails.method === "CASH")) {
          const paymentData: paymentLinkRequest = {
            amount: total,
            payment_provider: selectedPayment === "benefitpay" ? "benefit" : selectedPayment,
          };
          const paymentResponse = await service.requestPaymentLink(paymentData);

          if (
            paymentResponse.success &&
            paymentResponse.data?.payment_url &&
            paymentResponse.data?.track_id
          ) {
            setPaymentUrl(paymentResponse.data.payment_url);
            setTrackId(paymentResponse.data.track_id);
            sessionStorage.setItem("CurrentTrackID", paymentResponse.data.track_id);
            console.log(trackId);
            console.log(sessionStorage.getItem("CurrentTrackID"));
          } else {
            throw new Error("Failed to generate payment link");
          }
        }

        return response;
      }

      throw new Error("No response from server");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create order";
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setIsProcessing(false);


    }
  };

  const handleCashPayment = async (amountPaid: number) => {
    setIsProcessing(true);


    const paymentInProcessOrderId = localStorage.getItem("paymentInProcess");

    try {
      let orderId: string;

      // STEP 1: If payment in progress, use existing order ID
      if (paymentInProcessOrderId) {
        orderId = paymentInProcessOrderId;
      } else {
        // STEP 2: Otherwise, create a new order
        const newOrder = await createOrder({
          method: "CASH",
          amount: amountPaid,
          reference: '',
        });

        if (!newOrder?.data?.order.order_id) {
          throw new Error("Failed to create order");
        }
        orderId = newOrder?.data?.order.order_id;
      }

      // STEP 3: Update order status to paid
      const updateResult = await service.updateOrderPayment({
        order_id: orderId,
        payment: {
          method: "CASH",
          status: "paid",
          transaction_id: new Date().toISOString(), // Or generate proper ID
        },
      });
      await posService.updateOrderStatus({ order_id: orderId, status: "paid" });

      if (!updateResult.success) {
        throw new Error("Failed to mark order as paid");
      }

      // STEP 4: Show success
      setPaymentDetails({
        amountPaid,
        change: amountPaid - total,
      });
      setBillSummaryOpen(true);

      // STEP 5: Cleanup
      localStorage.removeItem("currentDraftId");

    } catch (err: any) {
      toast.error(err.message || "Error processing cash payment");
    } finally {
      setIsProcessing(false);


      setCashPaymentOpen(false);
    }
  };


  const handleCardPayment = async (success: boolean) => {
    setCardPaymentOpen(false);
    setIsProcessing(true);

    const paymentInProcessOrderId = localStorage.getItem("paymentInProcess");
    if (!success) {
      try {
        let orderId: string;
        if (paymentInProcessOrderId) {
          // If there is already an order in process, just navigate
          navigate("/pos/orders");
          return;
        } else {
          const draftId = localStorage.getItem("DraftId");
          if (draftId) {
            // If there is an active draft, update its status to 'pending_payment'
            await posService.updateOrderStatus({ order_id: draftId, status: "pending_payment" });
            navigate("/pos/orders");
            return;
          }
          // Otherwise, create a new order with status 'pending_payment'
          const newOrder = await createOrder({
            method: "CARD",
            amount: total,
            reference: new Date().getTime().toString(),
          }, false, true); // skipPaymentLink = true

          if (!newOrder?.data?.order?.order_id) {
            throw new Error("Failed to create order");
          }
          orderId = newOrder.data.order.order_id;
          localStorage.setItem("paymentInProcess", orderId);
          // Do not open summary dialog, just navigate
          navigate("/pos/orders");
          return;
        }
      } catch (err: any) {
        toast.error(err.message || "Error processing card payment");
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    try {
      let orderId: string;

      // STEP 1: If payment in progress, use existing order ID
      if (paymentInProcessOrderId) {
        orderId = paymentInProcessOrderId;
      } else {
        // STEP 2: Otherwise, create a new order
        const newOrder = await createOrder({
          method: "CARD",
          amount: total,
          reference: new Date().getTime().toString(),
        });

        if (!newOrder?.data?.order?.order_id) {
          throw new Error("Failed to create order");
        }
        orderId = newOrder.data.order.order_id;
      }

      // STEP 3: Update order payment status to paid
      const updateResult = await service.updateOrderPayment({
        order_id: orderId,
        payment: {
          method: "CARD",
          status: "paid",
          transaction_id: new Date().toISOString(), // Or use real transaction ID from provider
        },
      });
      await posService.updateOrderStatus({ order_id: orderId, status: "paid" });

      if (!updateResult.success) {
        throw new Error("Failed to mark order as paid");
      }

      // STEP 4: Show success
      setPaymentDetails({
        amountPaid: total,
      });
      setBillSummaryOpen(true);

      // STEP 5: Cleanup
      localStorage.removeItem("currentDraftId");

    } catch (err: any) {
      toast.error(err.message || "Error processing card payment");
    } finally {
      setIsProcessing(false);


    }
  };

  const handleBillSummaryClose = async () => {
    setBillSummaryOpen(false);

    // If dine-in, reset table status to "free"
    if (orderType === "dine_in" && selectedTable) {
      try {
        const result = await service.changeTableStatus(
          selectedTable.table_id,
          "free",
          {
            userId: user?.userId,
            role: userRole
          },
          ''
        );

        if (!result.success) {
          toast.warn("Table status could not be cleared");
        }
      } catch (err: any) {
        console.error("Error clearing table:", err.message);
        toast.error("Failed to clear table");
      }
    }

    clearCart();
    localStorage.removeItem("currentDraftId");
    navigate("/pos/orders");
  };

  const handlePrintReceipt = async () => {
    try {
      // TODO: Implement receipt printing
      toast.success("Order placed successfully!");
      handleBillSummaryClose();
    } catch (error) {
      console.error("Error printing receipt:", error);
      toast.error("Error printing receipt");
    }
  };

  React.useEffect(() => {
    if (!debouncedSearch.trim()) {
      setFilteredItems([]);
      return;
    }

    const query = debouncedSearch.toLowerCase();
    setIsFiltering(true);

    // Filter products locally instead of calling the backend
    const filtered = products.filter((product) =>
      product.name.toLowerCase().includes(query)
    );

    setFilteredItems(filtered);
    setIsFiltering(false);
  }, [debouncedSearch, products]);

  const handleApplyDiscount = (type: "PERCENTAGE" | "FIXED", value: number) => {
    if (!isCashier && !isSupervisor) {
      Snackbar({ open: true, message: "Only cashiers or supervisors can apply discounts" });
      return;
    }
    if (!discountThresholds) {
      toast.error("Discount thresholds not loaded");
      return;
    }

    if (value <= 0) {
      toast.error("Discount must be greater than zero");
      return;
    }

    let overThreshold = false;
    let maxAllowed = 0;

    if (type === "PERCENTAGE") {
      if (value > discountThresholds.cashierMaxPercentageDiscount) {
        overThreshold = true;
        maxAllowed = discountThresholds.cashierMaxPercentageDiscount;
      }
    } else {
      if (value > discountThresholds.cashierMaxFixedDiscount) {
        overThreshold = true;
        maxAllowed = discountThresholds.cashierMaxFixedDiscount;
      }
    }

    if (overThreshold) {
      // 🔒 Show Supervisor PIN modal
      setAppliedDiscountType(type);
      setAppliedDiscountValue(value);
      setShowPINModal(true);
      return;
    }

    // ✅ Apply directly
    applyDiscount(type, value);

    publicService.logAuditEvent({
      action: "apply_discount",
      resource: "/pos/order/discount",
      details: {
        order_id: orderId || "N/A",
        type,
        value,
        reason: "Auto-approved (within threshold)",
        subtotal,
        total,
      },
      status: "success",
      userId: user?.userId || "unknown",
      store_id: user?.store_id || "unknown",
    });
  };



  return (
    <SessionCheck>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
        }}
      >
        <AppBar
          position="static"
          elevation={0}
          sx={{ backgroundColor: "#1E8078" }}
        >
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
              Menu
            </Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Main Content Area */}
          {loading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >

              {/* Categories Skeleton */}
              <Stack spacing={2}>
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4, 5, 6].map((item) => (
                      <Grid item xs={12} sm={6} md={3} key={item}>
                        <Paper
                          elevation={1}
                          sx={{
                            p: 3,
                            height: "100%",
                            minHeight: 120,
                            backgroundColor: alpha(
                              theme.palette.background.paper,
                              0.95
                            ),
                            backdropFilter: "blur(8px)",
                            borderRadius: 1,
                            border: `1px solid ${alpha(
                              theme.palette.divider,
                              0.2
                            )}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Skeleton
                              variant="circular"
                              width={24}
                              height={24}
                            />
                            <Skeleton width={120} height={32} />
                          </Box>
                          <Skeleton width={80} height={24} sx={{ mt: 1 }} />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>


                <Divider sx={{ mb: 2 }} />

                {/* Menu Items Skeleton */}
                {!isPaymentInProgress && (
                  <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                    <Grid container spacing={2}>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
                          <Paper
                            elevation={1}
                            sx={{
                              p: 2,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              backgroundColor: alpha(
                                theme.palette.background.paper,
                                0.95
                              ),
                              backdropFilter: "blur(8px)",
                              borderRadius: 1,
                              border: `1px solid ${alpha(
                                theme.palette.divider,
                                0.2
                              )}`,
                            }}
                          >
                            <Skeleton
                              variant="text"
                              width="80%"
                              height={32}
                              sx={{ mb: 1 }}
                            />
                            <Skeleton
                              variant="text"
                              width="40%"
                              height={24}
                              sx={{ mb: 2 }}
                            />
                            <Skeleton
                              variant="text"
                              width="30%"
                              height={32}
                              sx={{ mb: 2 }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: "auto",
                              }}
                            >
                              <Skeleton
                                variant="circular"
                                width={32}
                                height={32}
                              />
                              <Skeleton width={40} height={32} />
                              <Skeleton
                                variant="circular"
                                width={32}
                                height={32}
                              />
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Stack>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {!isPaymentInProgress && (
                <Stack spacing={2}>
                  {/* Categories */}
                  <Box sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper
                          elevation={1}
                          onClick={() => setSelectedCategory(null)}
                          sx={{
                            p: 3,
                            height: "100%",
                            minHeight: 120,
                            cursor: "pointer",
                            backgroundColor: !selectedCategory
                              ? alpha(theme.palette.primary.main, 0.1)
                              : alpha(theme.palette.background.paper, 0.95),
                            backdropFilter: "blur(8px)",
                            borderRadius: 1,
                            transition: "all 0.2s",
                            border: `1px solid ${!selectedCategory
                              ? theme.palette.primary.main
                              : alpha(theme.palette.divider, 0.2)
                              }`,
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.1
                              ),
                              transform: "translateY(-2px)",
                              boxShadow: theme.shadows[2],
                            },
                          }}
                        >
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                          >
                            <Icon>restaurant_menu</Icon>
                            <Typography variant="h6">All Items</Typography>
                          </Box>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {products.length} items
                          </Typography>
                        </Paper>
                      </Grid>
                      {categories.map((category) => (
                        <Grid item xs={12} sm={6} md={3} key={category.id}>
                          <Paper
                            elevation={1}
                            onClick={() => setSelectedCategory(category.id)}
                            sx={{
                              p: 3,
                              height: "100%",
                              minHeight: 120,
                              cursor: "pointer",
                              backgroundColor:
                                selectedCategory === category.id
                                  ? alpha(category.color, 0.1)
                                  : alpha(theme.palette.background.paper, 0.95),
                              backdropFilter: "blur(8px)",
                              borderRadius: 1,
                              transition: "all 0.2s",
                              border: `1px solid ${selectedCategory === category.id
                                ? category.color
                                : alpha(theme.palette.divider, 0.2)
                                }`,
                              "&:hover": {
                                backgroundColor: alpha(category.color, 0.1),
                                transform: "translateY(-2px)",
                                boxShadow: theme.shadows[2],
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Icon sx={{ color: category.color }}>
                                {category.icon}
                              </Icon>
                              <Typography variant="h6">
                                {category.name}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {category.itemCount} items
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {/* Menu Items */}
                  {/* Search Bar */}
                  <Box sx={{ px: 2, py: 1 }}>
                    <TextField
                      id="menu-search-input"
                      fullWidth
                      variant="outlined"
                      placeholder="Search by Product Name"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <Icon sx={{ mr: 1, color: "text.secondary" }}>search</Icon>
                        ),
                        endAdornment: searchQuery && (
                          <IconButton size="small" onClick={() => setSearchQuery("")}>
                            <Icon>clear</Icon>
                          </IconButton>
                        ),
                      }}
                      sx={{
                        bgcolor: alpha(theme.palette.background.paper, 0.8),
                        borderRadius: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1,
                          height: 40,
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                    <Grid container spacing={2}>
                      {(() => {
                        // Show original menu items while typing but before debounced search starts
                        if (!debouncedSearch && searchQuery) {
                          return menuItems.map((item) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                              <Paper
                                elevation={1}
                                onClick={() => {
                                  setSelectedItem(item);
                                  setQuantity(1);
                                }}
                                sx={{
                                  p: 2,
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  cursor: "pointer",
                                  "&:hover": {
                                    boxShadow: theme.shadows[4],
                                    transform: "scale(1.01)",
                                  },
                                }}
                              >
                                <Typography variant="h6" gutterBottom>
                                  {item.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 2 }}
                                >
                                  {item.category}
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ mb: 2, color: "#1E8078" }}
                                >
                                  BHD {item.price.toFixed(3)}
                                </Typography>
                              </Paper>
                            </Grid>
                          ));
                        }

                        // Show loading spinner only after debounce starts
                        if (isFiltering && debouncedSearch) {
                          return (
                            <Box sx={{ textAlign: "center", mt: 4 }}>
                              <CircularProgress size={20} />
                            </Box>
                          );
                        }

                        // Show no results only if filteredItems is truly empty
                        if (filteredItems.length === 0 && debouncedSearch) {
                          return (
                            <Typography color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
                              No items found
                            </Typography>
                          );
                        }


                        return displayItems.map((item) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                            <Paper
                              elevation={1}
                              onClick={() => {
                                setSelectedItem(item);
                                setQuantity(1);
                              }}
                              sx={{
                                p: 2,
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                cursor: "pointer",
                                "&:hover": {
                                  boxShadow: theme.shadows[4],
                                  transform: "scale(1.01)",
                                },
                              }}
                            >
                              <Typography variant="h6" gutterBottom>
                                {item.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 2 }}
                              >
                                {item.category}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{ mb: 2, color: "#1E8078" }}
                              >
                                BHD {item.price.toFixed(3)}
                              </Typography>
                            </Paper>
                          </Grid>
                        ));
                      })()}
                    </Grid>
                  </Box>
                </Stack>
              )}
            </Box>
          )}

          {/* Cart */}
          {orderType && orderType === "dine_in" && !selectedTable ? (
            <Paper
              elevation={0}
              sx={{
                width: 400,
                display: "flex",
                flexDirection: "column",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(8px)",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  backdropFilter: "blur(8px)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Table Selection
                </Typography>
                <Typography>Current mode: {getOrderTypeLabel(orderType)}</Typography>
                {!isWaiter && !isPaymentInProgress && (
                  <Button
                    size="small"
                    onClick={() => setShowConfirm(true)}
                    sx={{ color: "#1E8078", textTransform: "none" }}
                  >
                    Change Order Mode
                  </Button>
                )
                }
              </Box>

              {/* Replaceable Content */}
              <Box sx={{ flex: 1, overflow: "auto", mt: 1, p: 2 }}>
                <TableSelector
                  onSelectTable={(table) => {
                    if (selectedTable && selectedTable !== table) {
                      setNewSelectedTable(table);
                      setShowTableConfirm(true);
                    } else {
                      setSelectedTable(table);
                    }
                  }}
                />
              </Box>

            </Paper>
          ) : orderType && orderType === "delivery" && deliveryInfo === null ? (
            <Paper
              elevation={0}
              sx={{
                width: 400,
                display: "flex",
                flexDirection: "column",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(8px)",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  backdropFilter: "blur(8px)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Delivery Info
                </Typography>
                <Typography>Current mode: {getOrderTypeLabel(orderType)}</Typography>
                <Box sx={{ px: 2, pb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Server: {user?.fullName || `${user?.first_name} ${user?.last_name}` || "Unknown"}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => setShowConfirm(true)}
                  sx={{ color: "#1E8078", textTransform: "none" }}
                >
                  Change Order Mode
                </Button>
              </Box>

              {/* Replaceable Content */}
              <Box sx={{ flex: 1, overflow: "auto", mt: 1, p: 2 }}>
                <DeliveryForm onSubmit={(info) => setDeliveryInfo(info)} />
              </Box>

              {/* Mode Switch Dialog */}
              <Dialog open={showConfirm}>
                <DialogTitle>Confirm Mode Change</DialogTitle>
                <DialogContent>
                  <Typography>
                    Changing the order mode will remove the selected table or delivery info if not supported.
                    Your cart will remain unchanged. Are you sure?
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowConfirm(false)}>Cancel</Button>
                  <Button
                    onClick={() => {
                      setDeliveryInfo(null);
                      setSelectedTable(null);
                      setOrderType(null);
                      setShowConfirm(false);
                    }}
                    autoFocus
                  >
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>
            </Paper>
          ) : orderType ? (
            <Paper
              elevation={0}
              sx={{
                width: 400,
                display: "flex",
                flexDirection: "column",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(8px)",
              }}
            >
              {/* Cart Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  backdropFilter: "blur(8px)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Icon sx={{ color: "#1E8078", fontSize: 24 }}>shopping_cart</Icon>
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    Cart
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "#1E8078",
                      color: "white",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 6,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <span>{items.length}</span>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {items.length === 1 ? "item" : "items"}
                    </Typography>
                  </Box>
                </Box>
                <Typography>
                  Current mode: {getOrderTypeLabel(orderType)}
                  {selectedTable && ` - ${selectedTable.table_name}`}
                </Typography>
                <Box sx={{ px: 2, pb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Server: {user?.fullName || `${user?.first_name} ${user?.last_name}` || "Unknown"}
                  </Typography>
                </Box>
                {!isWaiter && !isPaymentInProgress && (
                  <Button
                    size="small"
                    onClick={() => setShowConfirm(true)}
                    sx={{ color: "#1E8078", textTransform: "none" }}
                  >
                    Change Order Mode
                  </Button>
                )}
              </Box>

              {/* Cart Items */}
              <Box sx={{ flex: 1, overflow: "auto", mt: 1 }}>
                {items.map((item, index) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      p: 1.5,
                      mx: 2,
                      my: 1,
                      backgroundColor: theme.palette.background.default,
                      backdropFilter: "blur(8px)",
                      borderRadius: 2,
                    }}
                  >
                    {/* Item Name and Quantity */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "#1E8078",
                            borderRadius: 5,
                            p: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              color: "white",
                              fontSize: "0.875rem",
                              mx: 1,
                              my: 0.1,
                            }}
                          >
                            {index + 1}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body1" fontWeight={600}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            x{item.quantity}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography fontWeight={600}>
                        BHD {(item.price * item.quantity).toFixed(3)}
                      </Typography>
                    </Box>

                    {/* Modifiers */}
                    {item.modifierDetails.length > 0 && (
                      <Box sx={{ mt: 1, ml: 4 }}>
                        {item.modifierDetails.map((mod, idx) => (
                          <Typography key={idx} variant="body2" color="text.secondary">
                            - {mod.name} (+{CURRENCY} {mod.price.toFixed(2)})
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {/* Edit & Delete Icons */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                      <IconButton size="small" onClick={() => !isPaymentInProgress && handleEditCartItem(item)} disabled={isPaymentInProgress}>
                        <Icon fontSize="small" color={isPaymentInProgress ? "disabled" : "action"}>edit</Icon>
                      </IconButton>
                      <IconButton size="small" onClick={() => !isPaymentInProgress && handleDeleteCartItem(item.id)} disabled={isPaymentInProgress}>
                        <Icon fontSize="small" color={isPaymentInProgress ? "disabled" : "action"}>delete</Icon>
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Cart Summary */}

              <Box
                sx={{ p: 2, bgcolor: alpha(theme.palette.background.paper, 0.9) }}
              >
                {orderType === "dine_in" && selectedTable && !isPaymentInProgress && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">
                      {selectedTable.table_name}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setShowTableModal(true)}
                      sx={{ color: "#1E8078", textTransform: "none" }}
                    >
                      Change Table
                    </Button>
                  </Box>
                )}
                <Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Waiter Only Button */}
                  {isWaiter && (
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={items.length === 0 || !hasPermission("pos.orders.send_to_kitchen", user?.newPermissions, user?.assignment_details?.id || "", user?.is_store_owner || false)}
                      onClick={handleSendToKitchen}
                      sx={{
                        bgcolor: "#1E8078",
                        "&:hover": { bgcolor: "#16655C" },
                      }}
                    >
                      Send to Kitchen
                    </Button>
                  )}

                  {/* Cashier + Supervisor Buttons */}
                  {!isWaiter && (
                    <>
                      {/* Apply Discount – Only for cashier, supervisor, admin */}
                      {(discountThresholds && !isWaiter) && !isPaymentInProgress && (
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                            <TextField
                              label="Apply Discount"
                              type="number"
                              size="small"
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                if (val >= 0) {
                                  setAppliedDiscountValue(val);
                                }
                              }}
                              value={appliedDiscountValue ?? ""}
                              fullWidth
                              inputProps={{ min: 0, step: 0.1 }}
                            />
                            <Select
                              value={appliedDiscountType || ""}
                              onChange={(e) =>
                                setAppliedDiscountType(e.target.value as "PERCENTAGE" | "FIXED")
                              }
                              size="small"
                              renderValue={(value) => {
                                if (!value) return "Type";
                                if (value === "PERCENTAGE") return "%";
                                if (value === "FIXED") return "BHD";
                                return "Type";
                              }}
                            >
                              <MenuItem value="PERCENTAGE">%</MenuItem>
                              <MenuItem value="FIXED">BHD</MenuItem>
                            </Select>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                if (!appliedDiscountType || appliedDiscountValue == null) {
                                  toast.error("Please select discount type and value");
                                  return;
                                }

                                handleApplyDiscount(appliedDiscountType, appliedDiscountValue);
                              }}
                            >
                              Apply
                            </Button>
                            {appliedDiscount && (
                              <Button
                                variant="outlined"
                                color="error"
                                onClick={() => {
                                  setAppliedDiscount(null);
                                  toast.info("Discount removed");
                                }}
                              >
                                Remove
                              </Button>
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* Payment Method Selector – Only for cashier, supervisor, admin */}
                      {(isCashier || isSupervisor) && (
                        <>
                          <Box
                            sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
                          >
                            <Typography color="text.secondary">Subtotal</Typography>
                            <Typography>BHD {subtotal.toFixed(3)}</Typography>
                          </Box>
                          {appliedDiscount && (
                            <Box
                              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
                            >
                              <Typography color="text.secondary">
                                Discount{" "}
                                {appliedDiscount.type === "PERCENTAGE"
                                  ? `${appliedDiscount.value}%`
                                  : `BHD ${appliedDiscount.value.toFixed(3)}`}
                              </Typography>
                              <Typography>
                                - BHD{" "}
                                {(appliedDiscount.type === "PERCENTAGE"
                                  ? (total * appliedDiscount.value) / 100
                                  : appliedDiscount.value
                                ).toFixed(3)}
                              </Typography>
                            </Box>
                          )}
                          <Box
                            sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
                          >
                            <Typography color="text.secondary">VAT 10%</Typography>
                            <Typography>BHD {tax.toFixed(3)}</Typography>
                          </Box>
                          <Divider sx={{ mb: 2 }} />
                          <Box
                            sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
                          >
                            <Typography variant="h6">Total</Typography>
                            <Typography variant="h6">BHD {total.toFixed(3)}</Typography>
                          </Box>

                          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                            {!isPaymentInProgress && (
                              <Button
                                variant="outlined"
                                fullWidth
                                size="large"
                                disabled={items.length === 0}
                                onClick={handleSaveDraft}
                                sx={{
                                  color: "#1E8078",
                                  borderColor: "#1E8078",
                                  "&:hover": {
                                    backgroundColor: alpha("#1E8078", 0.1),
                                    borderColor: "#1E8078",
                                  },
                                }}
                              >
                                Save Draft
                              </Button>
                            )}

                            {selectedTable && !isPaymentInProgress && (
                              <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={items.length === 0 || isProcessing || !hasPermission("pos.orders.send_to_kitchen", user?.newPermissions, user?.assignment_details?.id || "", user?.is_store_owner || false)}
                                onClick={handleSendToKitchen}
                                sx={{
                                  backgroundColor: "#1E8078",
                                  color: "white",
                                  "&:hover": { backgroundColor: "#16655C" },
                                }}
                              >
                                {isProcessing ? <CircularProgress size={24} color="inherit" /> : "Send to Kitchen"}
                              </Button>
                            )}
                          </Stack>
                          {/* Payment Methods */}
                          <Typography color="text.secondary" sx={{ mb: 1 }}>
                            Payment Method
                          </Typography>
                          <PaymentMethodSelector
                            selectedPayment={selectedPayment}
                            onSelectPayment={setSelectedPayment}
                          />
                        </>
                      )}

                      {/* Place Order – Only for cashier, supervisor, admin */}
                      {(isCashier || isSupervisor) && (
                        <Button
                          variant="contained"
                          fullWidth
                          disabled={items.length === 0}
                          onClick={handlePlaceOrder}
                          sx={{
                            bgcolor: "#1E8078",
                            "&:hover": { bgcolor: "#16655C" },
                          }}
                        >
                          {isPaymentInProgress ? "Pay" : "Place Order"}
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                width: 400,
                display: "flex",
                flexDirection: "column",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(8px)",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  backdropFilter: "blur(8px)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Select Order Type
                </Typography>
              </Box>

              {/* Replaceable Content */}
              <Box sx={{ flex: 1, overflow: "auto", mt: 1, p: 2 }}>
                <OrderModeSelector onSelect={(mode) => setOrderType(mode)} />
              </Box>
            </Paper>
          )}
          {/* Mode Switch Dialog */}
          <Dialog open={showConfirm}>
            <DialogTitle>Confirm Mode Change</DialogTitle>
            <DialogContent>
              <Typography>
                Changing the order mode will remove the selected table or delivery info if not supported.
                Your cart will remain unchanged. Are you sure?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowConfirm(false)}>Cancel</Button>
              <Button
                onClick={() => {


                  setDeliveryInfo(null);
                  setSelectedTable(null);
                  setOrderType(null);
                  setDeliveryInfo(null);
                  setShowConfirm(false);
                }}
                autoFocus
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
          {/* Table Change Confirmation Dialog */}
          <Dialog open={showTableConfirm}>
            <DialogTitle>Confirm Table Change</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to change to another table?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowTableConfirm(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  if (newSelectedTable) {
                    setSelectedTable(newSelectedTable);
                  }
                  setShowTableConfirm(false);
                }}
                autoFocus
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
          {/* Table Selection Modal */}
          <Dialog
            open={showTableModal}
            onClose={() => setShowTableModal(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Select a New Table</DialogTitle>
            <DialogContent>
              <TableSelector
                onSelectTable={(table) => {
                  if (selectedTable && selectedTable.table_id !== table.table_id) {
                    setNewSelectedTable(table);
                    setShowTableModal(false);
                    setShowTableConfirm(true);
                  } else {
                    setSelectedTable(table);
                    setShowTableModal(false);
                  }
                }}
              />
            </DialogContent>
          </Dialog>
        </Box>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Loading Overlay */}
      {isProcessing && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: "blur(10px)",
              borderRadius: 2,
            }}
          >
            <CircularProgress />
            <Typography>Processing Order...</Typography>
          </Paper>
        </Box>
      )}

      {/* Payment Dialogs */}
      {cashPaymentOpen && (<CashPaymentDialog
        open={cashPaymentOpen}
        onClose={() => setCashPaymentOpen(false)}
        onConfirm={handleCashPayment}
        totalAmount={total}
      />)}

      {/* QR and instructions rendered clearly */}
      {qrPaymentOpen && (
        <QRPaymentDialog
          open={qrPaymentOpen}
          onClose={() => setQrPaymentOpen(false)}
          onConfirm={handleQrPayment}
          totalAmount={total}
          paymentMethod={qrPaymentTypeRef.current === "mastercard" ? "card" : "benefitpay"}
          paymentUrl={paymentUrl}
          paid={paymentConfirmed}
        />
      )}

      {cardPaymentOpen && (<CardPaymentDialog
        open={cardPaymentOpen}
        onClose={() => setCardPaymentOpen(false)}
        onConfirm={handleCardPayment}
        totalAmount={total}
        paymentMethod={selectedPayment === "mastercard" ? "card" : "benefitpay"}
      />)}

      <BillSummaryDialog
        open={billSummaryOpen}
        onClose={handleBillSummaryClose}
        onPrint={handlePrintReceipt}
        items={items}
        subtotal={subtotal}
        tax={tax}
        total={total}
        paymentMethod={selectedPayment}
        amountPaid={paymentDetails.amountPaid}
        change={paymentDetails.change}
        discount={appliedDiscount}
        orderId={orderId}
        orderType={orderType}
        tableNumber={selectedTable?.table_name}
        serverName={user?.fullName || "Unknown"}
        timestamp={new Date()}
        deliveryInfo={deliveryInfo}
      />
      {/* Item Details Modal */}
      <ItemDetailsModal
        item={selectedItem}
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        onAdd={(itemWithModifiers) => {
          if (!selectedItem) return;
          addItem(selectedItem, itemWithModifiers.modifiers);
        }}
      />
      <SupervisorPinModal
        action='apply_discount'
        open={showPINModal}
        onClose={() => setShowPINModal(false)}
        onSuccess={(reason) => {

          setAppliedDiscount({
            type: appliedDiscountType!,
            value: appliedDiscountValue,
            reason,
          });
          setReasonForDiscount(reason);
          publicService.logAuditEvent({
            action: "apply_discount_over_threshold",
            resource: "/pos/order/discount",
            details: {
              order_id: orderId || "N/A",
              type: appliedDiscountType,
              value: appliedDiscountValue,
              reason,
              subtotal,
              total,
            },
            status: "success",
            userId: user?.userId || "unknown",
            store_id: user?.store_id || "unknown",
          });
          toast.success("Supervisor approved. Discount applied.");
        }}
        reasonLabel="Why apply discount?"
      />
      {showConfirmDialog && (
        <ConfirmActionDialog
          open={showConfirmDialog.open}
          title={showConfirmDialog.title}
          description={showConfirmDialog.description}
          confirmText={showConfirmDialog.confirmText}
          cancelText={showConfirmDialog.cancelText}
          icon={showConfirmDialog.icon}
          color={showConfirmDialog.color}
          onClose={closeConfirmDialog}
          onConfirm={() => {
            showConfirmDialog.onConfirm();
            closeConfirmDialog();
          }}
        />
      )}
      <Dialog open={openQrOptionModal} onClose={() => setOpenQrOptionModal(false)}>
        <DialogTitle>Choose Payment Type</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Select payment method for QR:</Typography>

          <Button
            onClick={() => {
              const chosenType = "mastercard";
              setQrPaymentType(chosenType);
              qrPaymentTypeRef.current = chosenType;
              if (resolveQrPromise) resolveQrPromise(chosenType);
              setOpenQrOptionModal(false);
            }}
          >
            Mastercard
          </Button>

          <Button
            onClick={() => {
              const chosenType = "benefitpay";
              setQrPaymentType(chosenType);
              qrPaymentTypeRef.current = chosenType;
              if (resolveQrPromise) resolveQrPromise(chosenType);
              setOpenQrOptionModal(false);
            }}
          >
            BenefitPay
          </Button>
        </DialogContent>
      </Dialog>
    </SessionCheck>
  );
};
